import { useState, useEffect, useRef, useCallback } from 'react'
import { useManualQuery } from 'graphql-hooks'
import dayjs from 'dayjs'
import { getUserDashboardData, getCompanyAllUsers } from '../graphql/userDashboardQueries'
import { IGetUser } from '../../../types/custom-queries'
import { dashboardCache, CACHE_KEYS } from '../utils/dashboardCache'

interface UseUserDashboardDataProps {
  userId: string
  autoFetch?: boolean
}

export interface TeamLeaveData {
  id: string
  user: {
    id: string
    name: string
    imageUrl?: string
  }
  leaveType: {
    name: string
    color: string
  }
  startDate: string
  endDate: string
  isPartDay: boolean
  workingDays: number
}

interface Location {
  id: string
  name: string
  holidays?: Array<{
    year: number
    holidays: Array<{
      name: string
      date: string
      isHalfDay?: boolean
    }>
  }>
}

interface UserDashboardData {
  user: IGetUser | null
  company: { id: string; hourlyLeaveAccounting: boolean } | null
  teamLeaves: TeamLeaveData[]
  companyLeaves: TeamLeaveData[]
  teamUsers: Array<{ id: string; name: string; status: string; locationId?: string }>
  locations: Location[]
  visibilityAddon: Array<{ visibilityLevel: string[] }> | null
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
  fetchCompanyWideData: () => Promise<{ 
    leaves: TeamLeaveData[]
    users: Array<{ id: string; name: string; status: string; locationId?: string }>
    locations: Location[]
  }>
  isFromCache: boolean
  cacheAge: number | null
}

export const useUserDashboardData = ({ 
  userId, 
  autoFetch = true,
}: UseUserDashboardDataProps): UserDashboardData => {
  const [user, setUser] = useState<IGetUser | null>(null)
  const [company, setCompany] = useState<{ id: string; hourlyLeaveAccounting: boolean } | null>(null)
  const [teamLeaves, setTeamLeaves] = useState<TeamLeaveData[]>([])
  const [companyLeaves, setCompanyLeaves] = useState<TeamLeaveData[]>([])
  const [teamUsers, setTeamUsers] = useState<Array<{ id: string; name: string; status: string; locationId?: string }>>([])
  const [locations, setLocations] = useState<Location[]>([])
  const [visibilityAddon, setVisibilityAddon] = useState<Array<{ visibilityLevel: string[] }> | null>(null)
  const [isLoading, setIsLoading] = useState(autoFetch)
  const [error, setError] = useState<Error | null>(null)
  const [isFromCache, setIsFromCache] = useState(false)
  const [cacheAge, setCacheAge] = useState<number | null>(null)

  const abortControllerRef = useRef<AbortController>(new AbortController())
  const isMountedRef = useRef<boolean>(true)

  const [fetchDashboardData] = useManualQuery(getUserDashboardData, {
    fetchOptionsOverrides: {
      signal: abortControllerRef.current?.signal,
    },
  })

  const [fetchCompanyUsers] = useManualQuery(getCompanyAllUsers, {
    fetchOptionsOverrides: {
      signal: abortControllerRef.current?.signal,
    },
  })

  // Load data from cache immediately
  const loadFromCache = useCallback(() => {
    if (!userId) return false

    let hasCache = false

    // Try to load user data from cache
    const cachedUser = dashboardCache.get<IGetUser>(CACHE_KEYS.userData(userId))
    if (cachedUser) {
      setUser(cachedUser)
      hasCache = true
      
      // Extract team users from cached user data
      if (cachedUser.team?.users) {
        setTeamUsers(cachedUser.team.users)
      }
    }

    // Try to load company data from cache
    const cachedCompany = dashboardCache.get<any>(CACHE_KEYS.companyData())
    if (cachedCompany) {
      setCompany(cachedCompany)
      hasCache = true
    }

    // Try to load team leaves from cache
    const cachedTeamLeaves = dashboardCache.get<TeamLeaveData[]>(CACHE_KEYS.teamLeaves(userId))
    if (cachedTeamLeaves) {
      setTeamLeaves(cachedTeamLeaves)
      hasCache = true
    }

    // Try to load company leaves from cache
    const cachedCompanyLeaves = dashboardCache.get<TeamLeaveData[]>(CACHE_KEYS.companyLeaves())
    if (cachedCompanyLeaves) {
      setCompanyLeaves(cachedCompanyLeaves)
      hasCache = true
    }

    // Try to load locations from cache
    const cachedLocations = dashboardCache.get<Location[]>(CACHE_KEYS.locations())
    if (cachedLocations) {
      setLocations(cachedLocations)
      hasCache = true
    }

    // Try to load visibility addon from cache
    const cachedVisibility = dashboardCache.get<any>(CACHE_KEYS.visibilityAddon())
    if (cachedVisibility) {
      setVisibilityAddon(cachedVisibility)
      hasCache = true
    }

    if (hasCache) {
      setIsFromCache(true)
      const age = dashboardCache.getAge(CACHE_KEYS.userData(userId))
      setCacheAge(age)
    }

    return hasCache
  }, [userId])

  const loadUserData = async () => {
    if (!userId || !isMountedRef.current) return

    try {
      setIsLoading(true)
      setError(null)

      const today = dayjs().format('YYYY-MM-DD')
      const dateEnd = dayjs().add(14, 'days').format('YYYY-MM-DD') // Next 2 weeks
      
      // Performance tracking
      const startTime = performance.now()
      
      // Single GraphQL query to fetch all dashboard data
      const { data, error: queryError, loading } = await fetchDashboardData({
        variables: {
          userId,
          date: today,
          pendingLeavesDate: today,
          dateStart: today,
          dateEnd,
        },
      })

      const queryTime = performance.now() - startTime
      
      // Track query performance
      if (typeof window !== 'undefined' && (window as any).analytics?.track) {
        (window as any).analytics.track('dashboard_data_fetch', {
          query: 'getUserDashboard',
          response_time_ms: Math.round(queryTime),
          data_size_kb: data ? JSON.stringify(data).length / 1024 : 0,
          from_cache: false,
          single_query: true, // New metric to track unified query usage
          cache_hit: isFromCache, // Track if cache was used initially
          cache_age_ms: cacheAge,
        })
      }

      if (queryError) {
        // Better error handling for GraphQL errors
        let errorMessage = 'Failed to fetch dashboard data'
        
        // Handle different error structures
        if (typeof queryError === 'object' && queryError !== null) {
          const error = queryError as any
          
          if (error.graphQLErrors?.length > 0) {
            errorMessage = error.graphQLErrors[0].message || error.graphQLErrors[0]
          } else if (error.networkError) {
            errorMessage = `Network error: ${error.networkError.message || error.networkError}`
          } else if (error.message) {
            errorMessage = error.message
          } else {
            errorMessage = JSON.stringify(queryError)
          }
        }

        throw new Error(errorMessage)
      }

      if (!isMountedRef.current) return

      // Process user data
      if (data?.getUser) {
        setUser(data.getUser)
        // Cache user data
        dashboardCache.set(CACHE_KEYS.userData(userId), data.getUser)
      } else {
        throw new Error('No user data returned')
      }

      // Process company data
      if (data?.getCompany) {
        setCompany(data.getCompany)
        // Cache company data
        dashboardCache.set(CACHE_KEYS.companyData(), data.getCompany)
      }

      // Process visibility addon data
      if (data?.getVisibilityAddon) {
        setVisibilityAddon(data.getVisibilityAddon)
        // Cache visibility data
        dashboardCache.set(CACHE_KEYS.visibilityAddon(), data.getVisibilityAddon)
      }

      // Process locations data
      if (data?.getLocationList) {
        setLocations(data.getLocationList)
        // Cache locations data
        dashboardCache.set(CACHE_KEYS.locations(), data.getLocationList)
      }

      // Process team users from user's team
      if (data?.getUser?.team?.users) {
        setTeamUsers(data.getUser.team.users)
      }

      // Process leaves data
      if (data?.getTeamLeaves?.leaveRequests) {
        // Store all company leaves (unfiltered)
        setCompanyLeaves(data.getTeamLeaves.leaveRequests)
        // Cache company leaves
        dashboardCache.set(CACHE_KEYS.companyLeaves(), data.getTeamLeaves.leaveRequests)
        
        // Filter leaves to only include team members
        if (data?.getUser?.team) {
          const teamMemberIds = data.getUser.team.users?.map(user => user.id) || 
                               data.getUser.team.approvers?.map(approver => approver.id) || []
          
          const filteredLeaves = data.getTeamLeaves.leaveRequests.filter(leave => 
            teamMemberIds.includes(leave.user.id)
          )
          
          setTeamLeaves(filteredLeaves)
          // Cache team leaves
          dashboardCache.set(CACHE_KEYS.teamLeaves(userId), filteredLeaves)
        }
      }
      
      // Mark that we're no longer showing cached data
      setIsFromCache(false)
      setCacheAge(null)
    } catch (err) {
      if (!isMountedRef.current) return
      
      let error: Error
      if (err instanceof Error) {
        error = err
      } else if (typeof err === 'object' && err !== null) {
        // Handle non-Error objects (like GraphQL errors)
        error = new Error(JSON.stringify(err, null, 2))
      } else {
        error = new Error(String(err) || 'Unknown error occurred')
      }
      
      setError(error)
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false)
      }
    }
  }

  const refetch = async () => {
    if (isMountedRef.current) {
      await loadUserData()
    }
  }

  const fetchCompanyWideData = async () => {
    try {
      const { data, error: queryError } = await fetchCompanyUsers({
        variables: {},
      })

      if (queryError) {
        console.error('Error fetching company-wide users:', queryError)
        throw queryError
      }

      const users = data?.getAllUserListPaginated?.users?.filter((user: any) => user.status === 'ACTIVE') || []
      const locations = data?.getLocationList || []

      // Return companyLeaves which contain unfiltered company-wide leave data, plus locations
      return { leaves: companyLeaves, users, locations }
    } catch (error) {
      console.error('Failed to fetch company-wide users:', error)
      // Return existing data on error to avoid breaking the UI
      return { leaves: companyLeaves, users: [], locations: [] }
    }
  }

  useEffect(() => {
    isMountedRef.current = true
    abortControllerRef.current = new AbortController()
    
    if (autoFetch && userId) {
      // Progressive loading strategy:
      // 1. Load from cache immediately (if available)
      const hasCache = loadFromCache()
      
      // 2. Set loading state appropriately
      //    - If we have cache, we're not "loading" from user perspective
      //    - But we still fetch fresh data in background
      setIsLoading(!hasCache)
      
      // 3. Always fetch fresh data (even if we have cache)
      loadUserData()
    }

    return () => {
      isMountedRef.current = false
      abortControllerRef.current?.abort()
    }
  }, [userId, autoFetch, loadFromCache])

  return {
    user,
    company,
    teamLeaves,
    companyLeaves,
    teamUsers,
    locations,
    visibilityAddon,
    isLoading,
    error,
    refetch,
    fetchCompanyWideData,
    isFromCache,
    cacheAge,
  }
}